import { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'

import { Faq } from '@ecm/data/Faq'
import { runEff } from '@ecm/effect'
import Dashboard from '@ecm/ui/component/block/Dashboard'
import FaqBlock from '@ecm/ui/component/block/Faq'
import OurCustomersBlock from '@ecm/ui/component/block/OurCustomers'
import Banner from '@ecm/ui/component/mobilePage/Banner'
import DownloadApp from '@ecm/ui/component/mobilePage/DownloadApp'
import Features from '@ecm/ui/component/mobilePage/Features'
import MobileAppButtons from '@ecm/ui/component/mobilePage/MobileAppButtons'
import NeedHelp from '@ecm/ui/component/NeedHelp'
import Color from '@ecm/ui/element/Color'
import Hero from '@ecm/ui/element/Hero'
import HeroList from '@ecm/ui/element/HeroList'
import MainContainer from '@ecm/ui/element/MainContainer'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'
import DefaultLayout from '@ecm/ui/layout/Default'

import checklistIcon from '/public/images/icon/checklist.svg'
import dolarsIcon from '/public/images/icon/dolars.svg'
import mailforwardIcon from '/public/images/icon/mail-forward.svg'
import shieldIcon from '/public/images/icon/shield.svg'
import mobileAppIllustration from '/public/images/illustration/mobile-app.svg'
import headerIllustration from '/public/images/illustration/mobile-hero.svg'

const DynamicVideo = dynamic(() => import('@ecm/ui/component/mobilePage/Video'))

const dashboardSteps = [
  {
    icon: shieldIcon,
    title: (
      <>
        <b>Two-factor authentication</b> for added security
      </>
    ),
  },
  {
    icon: mailforwardIcon,
    title: (
      <>
        <b>Forward mail or packages</b> with shipping services
      </>
    ),
  },
  {
    icon: dolarsIcon,
    title: (
      <>
        <b>Earn money on-the-go</b> with check deposit services
      </>
    ),
  },
  {
    icon: checklistIcon,
    title: (
      <>
        Instant access to <b>actionable account Insights</b>
      </>
    ),
  },
]

type MobilePageProps = {
  faqs: Faq[]
}

const MobilePage = ({ faqs }: MobilePageProps) => {
  const [videoVisibility, setVideoVisibility] = useState(false)
  const isSmallScreen = useSmallScreen()

  return (
    <DefaultLayout seoPage="mobile" hideFinalCta>
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
        }}
      >
        <Hero
          heading="Access your postal&nbsp; mail on the go"
          image={headerIllustration}
          onClickVideoButton={setVideoVisibility}
        >
          <Typography variant="p3" mb="0" maxWidth={{ md: '475px', xs: 'unset' }}>
            Download the Earth Class mail app and log into your account to
            <b> view, search, share, archive, and securely shred your mail</b> — plus much more!
            Access your digitized mail from
          </Typography>

          <HeroList />

          <Button
            variant="text"
            onClick={() => setVideoVisibility(true)}
            sx={{
              '&:after': {
                backgroundImage: "url('/images/icon/caret-right.svg')",
                content: "''",
                height: '12px',
                position: 'absolute',
                right: '-15px',
                top: '6px',
                transition: '200ms right ease-out',
                width: '8px',
              },
              '&:hover&:after': {
                right: '-25px',
              },
              position: 'relative',
            }}
          >
            <Typography variant="p3" fontWeight="700" color={Color.saint.normal} mb="0">
              {isSmallScreen ? 'Watch how it works' : ' Watch how it works on your mobile device'}
            </Typography>
          </Button>

          <MobileAppButtons />
        </Hero>

        <MainContainer sx={{ textAlign: 'center' }}>
          <Typography variant="h3">Running a business? Traveling? Moving?</Typography>
          <Typography variant="p2" color={Color.saint.lighter}>
            <b>No matter the reason — access your postal mail wherever you go!</b>
            <br />
            With secure access to your virtual mailbox from any device,
            <br />
            managing your postal mail is
            <b> easier than ever.</b>
          </Typography>
        </MainContainer>
      </Box>

      <Banner />

      <Features />

      <Dashboard
        heading="What makes our virtual mailbox mobile app different?"
        steps={dashboardSteps}
        illustration={mobileAppIllustration}
        headingMaxWidth="600px"
        sx={{ backgroundColor: Color.white.normal }}
      />

      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 950px)`,
        }}
      >
        <DownloadApp />
        <OurCustomersBlock variant="overline" greyIcons />
        <FaqBlock faqs={faqs} />
      </Box>

      <NeedHelp message='There are several ways you can' />

      <DynamicVideo isOpen={videoVisibility} onClose={() => setVideoVisibility(false)} />
    </DefaultLayout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const faqs: Faq[] = await runEff(async eff => {
    return await eff.getFaqsByPage(['/mobile'])
  })

  return { props: { faqs } }
}

export default MobilePage
